import { buildAd } from '../CampaignBuilder';
import { createCreative } from '../../../fbApi/Creative';
import constants from '../constants/constants';
import {
  createCanvasForCollection,
  createCollectionAdWithCanvas
} from '../../AdBuilder/AdCreator';
import {
  generateCanvasUrl,
  returnCanvasAdObjectWithCanvasUrl
} from '../PackageLauncher';
import { returnConversionDomainUrl } from '../../../utils/urls';

export const generateAndGetCanvasUrl = async (ad, form) => {
  const { id: adId, ad: innerAd } = ad;
  const { resource_id: resourceId } = innerAd;
  const { ads } = form;
  let destinationUrl = '';
  let canvasUrl = '';
  const adFields = ads.find((item) => item.id === parseInt(adId, 10));
  if (adFields) {
    destinationUrl = adFields.destinationUrl;
  }
  try {
    canvasUrl = await generateCanvasUrl(resourceId, destinationUrl, form);
  } catch (error) {
    canvasUrl = '';
    console.log('Error in generateAndGetCanvasUrl():', error);
  }
  return Promise.resolve(canvasUrl);
};

export const createCanvasAdWithCanvasUrl = async (
  ad,
  newAdSetId,
  form,
  canvasUrl,
  token
) => {
  const adData = returnCanvasAdObjectWithCanvasUrl(
    ad,
    newAdSetId,
    form,
    canvasUrl
  );
  let resultAd = '';
  resultAd = await buildAd(adData, token);

  let attempts = 0;

  while (attempts < 5 && resultAd.error) {
    attempts = attempts + 1;
    // eslint-disable-next-line no-await-in-loop
    resultAd = await buildAd(adData, token);
  }
  resultAd.name = adData.name;

  return resultAd;
};

export const createCanvasForCollectionAd = async (ad, form, token) => {
  const { ad: innerAd } = ad;
  const { asset_id: assetId, video, footer_text: footerText } = innerAd;
  const { ads, pageId, pageAccessToken, description } = form;
  const adFields = ads.find((item) => item.id === parseInt(ad.id, 10));
  const { productSet, footerUrl } = adFields || {};
  const collectionParamters = {
    productSet,
    pageId,
    videoId: assetId,
    pageToken: pageAccessToken,
    token,
    description,
    videoUrl: video,
    destinationUrl: footerUrl,
    buttonText: footerText
  };
  const canvasData = await createCanvasForCollection(collectionParamters);
  return Promise.resolve(canvasData);
};

export const createCollectionAdWithCanvasData = async (
  ad,
  newAdSetId,
  form,
  canvasData,
  token
) => {
  const { ad: innerAd } = ad;
  const {
    asset_id: assetId,
    title,
    message,
    thumbnail,
    video,
    footer_text: footerText,
    utm: adUtm
  } = innerAd;
  const {
    ads,
    accountId,
    pixel,
    pageId,
    // displayUrl,
    pageAccessToken,
    accountName,
    description,
    status,
    utmProfile,
    willUseConversionDomain
  } = form;
  const { collection } = utmProfile || {};
  const adFields = ads.find((item) => item.id === parseInt(ad.id, 10));
  const { productSet, footerUrl } = adFields || {};
  const adParameters = {
    adSetId: newAdSetId,
    accountId,
    name: ad.name.replace(constants.LAUNCHER.CLIENT_NAME, accountName),
    status,
    productSet,
    pageId,
    videoId: assetId,
    title,
    message,
    // displayLink: displayUrl,
    imageUrl: thumbnail,
    urlTags: collection || adUtm,
    pixel,
    token,
    pageToken: pageAccessToken,
    description,
    videoUrl: video,
    destinationUrl: footerUrl,
    buttonText: footerText,
    willUseConversionDomain
  };

  const resultAd = await createCollectionAdWithCanvas(adParameters, canvasData);
  resultAd.name = adParameters.name;

  return resultAd;
};

export const createAdCreativeForCarousel = async (ad, form, token) => {
  const {
    ads,
    accountId,
    pageId,
    // displayUrl,
    instagramAccount,
    utmProfile,
    formCardThumbnails
  } = form;

  const adFields = ads.find((item) => item.id === parseInt(ad.id, 10));
  const cards = [];

  ad.ad.cards.forEach((card) => {
    let cardFields = null;
    let foundCardThumbnail = card.thumbnail;
    if (adFields) {
      cardFields = adFields.cards.find((item) => item.cardId === card.id);
      if (formCardThumbnails) {
        foundCardThumbnail = formCardThumbnails.find((formCard) => {
          return formCard.id === cardFields.cardId;
        }).thumbnail;
      }
    }
    cards.push({
      link: cardFields ? cardFields.destinationUrl : '',
      picture: foundCardThumbnail,
      name: card.headline || '',
      description: card.description || '',
      call_to_action: {
        type: ad.ad.call_to_action,
        value: { link: cardFields ? cardFields.destinationUrl : '' }
      },
      video_id: card.asset_id
    });
  });

  const creativeParameters = {
    name: 'Creative',
    object_story_spec: {
      page_id: pageId,
      link_data: {
        // link: displayUrl,
        message: ad.ad.message,
        // caption: displayUrl,
        call_to_action: { type: ad.ad.call_to_action },
        child_attachments: cards,
        multi_share_end_card: false,
        multi_share_optimized: false
      }
    },
    degrees_of_freedom_spec: {
      creative_features_spec: {
        standard_enhancements: {
          enroll_status: 'OPT_OUT'
        }
      }
    },
    url_tags:
      utmProfile && utmProfile.carousel ? utmProfile.carousel : ad.ad.utm,
    instagram_actor_id: instagramAccount,
    access_token: token
  };

  const resultCreative = await createCreative(accountId, creativeParameters);

  return Promise.resolve(resultCreative);
};

export const createCarouselAdWIthCreative = async (
  ad,
  newAdSetId,
  form,
  adCreative,
  token
) => {
  const { accountId, pixel, accountName, status } = form;

  const { id: resultCreativeId } = adCreative;

  let resultAd = {};
  if (!resultCreativeId) {
    resultAd = {
      error: {
        error_user_title: 'Ad Creative Error',
        error_user_msg:
          'Ad creative generation failed. Check the carousel card thumbnails and other content or remove some cards, and then try again'
      }
    };
  } else {
    const adData = {
      adSetId: newAdSetId,
      accountId,
      name: ad.name.replace(constants.LAUNCHER.CLIENT_NAME, accountName),
      status,
      creative: { creative_id: resultCreativeId },
      tracking_specs: [
        {
          'action.type': [constants.LAUNCHER.OFFSITE_CONVERSIONS_VALUE],
          fb_pixel: [pixel]
        }
      ],
      conversion_domain: returnConversionDomainUrl({
        destinationUrl:
          form.cards && form.cards.length ? form.cards[0].destinationUrl : '',
        campaign: form.willUseConversionDomain
          ? { objective: constants.LAUNCHER.CONVERSIONS }
          : null
      })
    };

    resultAd = await buildAd(adData, token);

    let attempts = 0;

    while (attempts < 5 && resultAd.error) {
      attempts = attempts + 1;
      // eslint-disable-next-line no-await-in-loop
      resultAd = await buildAd(adData, token);
    }
    resultAd.name = adData.name;
  }
  return resultAd;
};



// WEBPACK FOOTER //
// ./src/screens/PackageBuilder/AdLibrary/AdLauncher.js